import React, { useState } from "react";
import { X, ArrowRight } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
export default function AuthModal({ open, onClose, onSuccess, beforeGoogle }) {
  const [mode, setMode] = useState("login");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [loading, setLoading] = useState(false);
  const { loginWithToken, setUserFromSession, revalidate } = useAuth();

  if (!open) return null;

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const endpoint = mode === "login" ? "/auth/login" : "/auth/signup";
      const body = mode === "login" ? { email, password } : { email, password, name };
      const { data } = await api.post(endpoint, body);
      loginWithToken(data.token, data.user);
      toast.success(`Welcome, ${data.user.name}`);
      onSuccess(data.user);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Authentication failed");
    } finally {
      setLoading(false);
    }
  };

  const googleLogin = () => {
    if (beforeGoogle) beforeGoogle();
    const redirectUrl = window.location.origin + "/auth/popup-callback";
    const url =
      "https://auth.emergentagent.com/?redirect=" + encodeURIComponent(redirectUrl);

    // Open a centered popup (fallback: full-page redirect if popup blocked)
    const w = 480;
    const h = 620;
    const y = window.top?.outerHeight
      ? Math.max(0, (window.top.outerHeight - h) / 2 + (window.top.screenY || 0))
      : 100;
    const x = window.top?.outerWidth
      ? Math.max(0, (window.top.outerWidth - w) / 2 + (window.top.screenX || 0))
      : 100;
    const popup = window.open(
      url,
      "bliencer_google_auth",
      `width=${w},height=${h},left=${x},top=${y}`,
    );
    if (!popup) {
      // Popups blocked — degrade gracefully to full-page redirect.
      const fallbackReturn = window.location.pathname + window.location.search;
      const fbRedirect = window.location.origin + fallbackReturn;
      window.location.href =
        "https://auth.emergentagent.com/?redirect=" + encodeURIComponent(fbRedirect);
      return;
    }
    setLoading(true);

    let done = false;
    const finish = async (payload) => {
      if (done) return;
      done = true;

      window.removeEventListener("message", onMessage);
      window.removeEventListener("storage", onStorage);
      if (bc) { try { bc.close(); } catch {} }
      window.clearInterval(pollClosed);
      window.clearInterval(pollAuth);
      setLoading(false);

      if (payload?.ok && payload.user) {
        setUserFromSession(payload.user);
        try { await revalidate(); } catch {}
        toast.success(`Welcome, ${payload.user.name}`);
        onSuccess(payload.user);
      } else if (payload?.ok) {
        // No user object but session cookie is live — pull it via revalidate
        try {
          const u = await revalidate();
          if (u) {
            toast.success(`Welcome, ${u.name}`);
            onSuccess(u);
          } else {
            toast.error("Google sign-in failed. Try again.");
          }
        } catch {
          toast.error("Google sign-in failed. Try again.");
        }
      } else {
        toast.error("Google sign-in failed. Try again.");
      }
    };

    // Channel 1: window postMessage (only works if opener wasn't severed)
    const onMessage = (event) => {
      if (event.origin !== window.location.origin) return;
      const msg = event.data;
      if (!msg || msg.source !== "bliencer-auth") return;
      finish(msg);
    };
    window.addEventListener("message", onMessage);

    // Channel 2: BroadcastChannel — same-origin, no opener needed
    let bc = null;
    try {
      bc = new BroadcastChannel("bliencer-auth");
      bc.onmessage = (event) => {
        const msg = event.data;
        if (!msg || msg.source !== "bliencer-auth") return;
        finish(msg);
      };
    } catch {}

    // Channel 3: localStorage ping — universal fallback
    const onStorage = (e) => {
      if (e.key !== "bliencer_auth_event" || !e.newValue) return;
      try {
        const msg = JSON.parse(e.newValue);
        finish({ source: "bliencer-auth", ...msg });
      } catch {}
    };
    window.addEventListener("storage", onStorage);

    // Fallback: while popup is open, poll /auth/me every 1.5s to detect
    // login even if all three channels above failed.
    const pollAuth = window.setInterval(async () => {
      try {
        const u = await revalidate();
        if (u) finish({ ok: true, user: u });
      } catch {}
    }, 1500);

    // Detect popup close without completion
    const pollClosed = window.setInterval(() => {
      if (popup.closed) {
        window.clearInterval(pollClosed);
        window.clearInterval(pollAuth);
        window.removeEventListener("message", onMessage);
        window.removeEventListener("storage", onStorage);
        if (bc) { try { bc.close(); } catch {} }
        if (!done) setLoading(false);
      }
    }, 700);
  };

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm"
      onClick={onClose}
      data-testid="auth-modal"
    >
      <div
        className="squircle-lg border border-[#2b2b2b] bg-[#111] w-full max-w-md p-8"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-6">
          <div>
            <div className="text-xs uppercase tracking-widest text-[#a0a0a0]">Almost there</div>
            <h2 className="text-2xl font-bold tracking-tight mt-1">
              {mode === "login" ? "Sign in to confirm" : "Create your account"}
            </h2>
          </div>
          <button
            onClick={onClose}
            data-testid="auth-modal-close"
            className="squircle-sm border border-[#2b2b2b] h-9 w-9 flex items-center justify-center hover:bg-[#0d0d0d] btn-anim"
            aria-label="Close"
          >
            <X size={14} />
          </button>
        </div>

        <div className="squircle-sm border border-[#2b2b2b] bg-[#0d0d0d] p-1 flex gap-1 mb-6">
          <button
            data-testid="modal-tab-login"
            onClick={() => setMode("login")}
            className={`flex-1 py-2 text-sm rounded-[14px] btn-anim ${
              mode === "login" ? "bg-white text-black" : "text-[#a0a0a0]"
            }`}
          >
            Log in
          </button>
          <button
            data-testid="modal-tab-signup"
            onClick={() => setMode("signup")}
            className={`flex-1 py-2 text-sm rounded-[14px] btn-anim ${
              mode === "signup" ? "bg-white text-black" : "text-[#a0a0a0]"
            }`}
          >
            Sign up
          </button>
        </div>

        <form onSubmit={submit} className="space-y-3" data-testid="modal-auth-form">
          {mode === "signup" && (
            <input
              required
              data-testid="modal-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Your name"
              className="w-full squircle-sm bg-transparent border border-[#2b2b2b] px-4 py-3 text-white focus:border-white outline-none"
            />
          )}
          <input
            required
            type="email"
            data-testid="modal-email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="Email"
            className="w-full squircle-sm bg-transparent border border-[#2b2b2b] px-4 py-3 text-white focus:border-white outline-none"
          />
          <input
            required
            type="password"
            minLength={6}
            data-testid="modal-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="Password"
            className="w-full squircle-sm bg-transparent border border-[#2b2b2b] px-4 py-3 text-white focus:border-white outline-none"
          />
          <button
            type="submit"
            disabled={loading}
            data-testid="modal-submit"
            className="w-full squircle bg-white text-black py-3 font-medium hover:bg-[#f2f2f2] btn-anim inline-flex items-center justify-center gap-2 disabled:opacity-50"
          >
            {loading
              ? "…"
              : mode === "login"
                ? "Sign in & confirm order"
                : "Create account & confirm"}
            <ArrowRight size={14} />
          </button>
        </form>

        <div className="my-5 flex items-center gap-3 text-xs text-[#666]">
          <span className="h-px flex-1 bg-[#1e1e1e]" /> OR{" "}
          <span className="h-px flex-1 bg-[#1e1e1e]" />
        </div>
        <button
          data-testid="modal-google"
          onClick={googleLogin}
          disabled={loading}
          className="w-full squircle border border-[#2b2b2b] py-3 font-medium hover:bg-[#0d0d0d] btn-anim disabled:opacity-50 inline-flex items-center justify-center gap-2"
        >
          {loading ? "Waiting for Google window…" : "Continue with Google"}
        </button>
        <p className="mt-3 text-[11px] text-[#7a7a7a] text-center">
          Opens in a small window — your order stays right here.
        </p>

        <p className="mt-6 text-xs text-[#a0a0a0] text-center">
          Your order is saved. Sign in only takes a second.
        </p>
      </div>
    </div>
  );
}
